/**
 * PlateCodesControl — Hub page showing 7 Emirates × 3 categories
 * (Private / Motorcycle / Classic). Each category has a toggle to enable/disable,
 * and clicking an ACTIVE category opens its codes manager.
 *
 * Backend: /api/admin/plate-codes/emirates  (admin)
 *          /api/admin/plate-codes/emirates/{emirate}/categories/{category}/toggle
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, MapPin, Car, Bike, Crown, Power, ArrowRight, RefreshCcw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/plate-codes`;

const CAT_ICON = {
  private: Car,
  motorcycle: Bike,
  classic: Crown,
};

export default function PlateCodesControl() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [emirates, setEmirates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingKey, setTogglingKey] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${BASE}/emirates`, { headers: getAuthHeaders() });
      setEmirates(res.data?.data || []);
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل البيانات' : 'Failed to load');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = async (emirate, category, isActive) => {
    const key = `${emirate.id}-${category.id}`;
    setTogglingKey(key);
    try {
      await axios.put(
        `${BASE}/emirates/${emirate.id}/categories/${category.id}/toggle`,
        { is_active: !isActive },
        { headers: getAuthHeaders() },
      );
      // optimistic local patch
      setEmirates((prev) =>
        prev.map((e) =>
          e.id === emirate.id
            ? {
                ...e,
                categories: e.categories.map((c) =>
                  c.id === category.id ? { ...c, is_active: !isActive } : c,
                ),
              }
            : e,
        ),
      );
      toast.success(
        !isActive
          ? (isAr ? `تم تفعيل ${category.name_ar} في ${emirate.name_ar}` : `${category.name_en} enabled in ${emirate.name_en}`)
          : (isAr ? `تم تعطيل ${category.name_ar} في ${emirate.name_ar}` : `${category.name_en} disabled in ${emirate.name_en}`),
      );
    } catch (e) {
      toast.error(isAr ? 'فشل التغيير' : 'Toggle failed');
    } finally {
      setTogglingKey(null);
    }
  };

  const totalActive = emirates.reduce(
    (sum, e) => sum + e.categories.filter((c) => c.is_active).length,
    0,
  );
  const totalCodes = emirates.reduce(
    (sum, e) => sum + e.categories.reduce((cs, c) => cs + (c.count || 0), 0),
    0,
  );

  return (
    <DashboardLayout
      title="التحكم بأكواد لوحات السيارات"
      titleEn="Plate Codes Control"
      subtitle="تفعيل/تعطيل فئات اللوحات لكل إمارة وإدارة الأكواد"
      subtitleEn="Toggle plate categories per emirate and manage codes"
      screenCode="BIZ-C04"
      backPath="/control/cloudgm-business"
      onBack={() => navigate('/control/cloudgm-business')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'CLOUDGM FZ LLC (للتحكم)', labelEn: 'CLOUDGM FZ LLC', path: '/control/cloudgm-business' },
        { labelAr: 'أكواد لوحات السيارات', labelEn: 'Plate Codes' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="plate-codes-control">

        {/* Header summary */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <div className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-center gap-1.5">
              <Power className="h-3 w-3" />
              {isAr ? 'مفعّلة' : 'Active'}:{' '}
              <span className="font-bold">{totalActive}</span> / {emirates.length * 3}
            </div>
            <div className="px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 flex items-center gap-1.5">
              <Car className="h-3 w-3" />
              {isAr ? 'إجمالي الأكواد' : 'Total codes'}:{' '}
              <span className="font-bold">{totalCodes}</span>
            </div>
          </div>
          <Button
            onClick={() => fetchData(true)}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5 gap-2"
            data-testid="refresh-emirates"
          >
            <RefreshCcw className="h-4 w-4" />
            {isAr ? 'تحديث' : 'Refresh'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="emirates-grid">
            {emirates.map((e, idx) => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.04, 0.3) }}
                className="bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-4 hover:border-white/20 transition-colors"
                style={{ borderColor: `${e.color}30` }}
                data-testid={`emirate-card-${e.id}`}
              >
                {/* Emirate header */}
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center border"
                    style={{ backgroundColor: `${e.color}15`, borderColor: `${e.color}40` }}
                  >
                    <MapPin className="h-5 w-5" style={{ color: e.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-white truncate">{isAr ? e.name_ar : e.name_en}</p>
                    <p className="text-[10px] text-[#71717A]" dir="ltr">{e.name_en}</p>
                  </div>
                </div>

                {/* 3 category rows */}
                <div className="space-y-2">
                  {e.categories.map((c) => {
                    const Icon = CAT_ICON[c.id] || Car;
                    const key = `${e.id}-${c.id}`;
                    const isToggling = togglingKey === key;
                    return (
                      <div
                        key={c.id}
                        className={`rounded-xl border p-3 transition-colors ${
                          c.is_active
                            ? 'bg-white/[0.03] border-white/10 hover:border-white/20'
                            : 'bg-white/[0.01] border-white/5 opacity-70'
                        }`}
                        data-testid={`cat-row-${e.id}-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (!c.is_active) return;
                              navigate(`/control/cloudgm-business/plate-codes/${e.id}/${c.id}`);
                            }}
                            disabled={!c.is_active}
                            className="flex items-center gap-2 flex-1 min-w-0 text-start disabled:cursor-not-allowed"
                            data-testid={`cat-open-${e.id}-${c.id}`}
                          >
                            <Icon
                              className="h-4 w-4 shrink-0"
                              style={{ color: c.is_active ? c.color : '#52525b' }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold truncate ${c.is_active ? 'text-white' : 'text-[#71717A]'}`}>
                                {isAr ? c.name_ar : c.name_en}
                              </p>
                              <p className="text-[10px] text-[#71717A]">
                                {c.count || 0} {isAr ? 'كود' : 'codes'}
                              </p>
                            </div>
                            {c.is_active && (
                              <ArrowRight
                                className={`h-3.5 w-3.5 text-[#71717A] shrink-0 ${isAr ? 'rotate-180' : ''}`}
                              />
                            )}
                          </button>
                          <div className="shrink-0">
                            {isToggling ? (
                              <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                            ) : (
                              <Switch
                                checked={c.is_active}
                                onCheckedChange={() => toggle(e, c, c.is_active)}
                                data-testid={`toggle-${e.id}-${c.id}`}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
